import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { authService } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import { useColorMode } from '../context/ColorModeContext';

function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleColorMode, mode } = useColorMode();
  
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Récupérer l'ID utilisateur et l'email depuis la navigation (state)
  const { userId, email } = location.state || {};

  useEffect(() => {
    if (!userId) {
      // Si on n'a pas d'ID, on ne peut pas vérifier. Redirection vers login.
      // On pourrait aussi permettre d'entrer l'email si le backend le supportait.
      navigate('/login');
    }
  }, [userId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verifyEmail({ userId, code });

      // Stocker le token et les infos utilisateur retournés par la vérification
      if (response.data.token) {
        localStorage.setItem('era_token', response.data.token);
        localStorage.setItem('era_user', JSON.stringify(response.data.user));
        
        // Rediriger vers le dashboard
        navigate('/dashboard');
      } else {
        // Fallback vers login si pas de token (ne devrait pas arriver avec l'implémentation actuelle)
        navigate('/login', { 
          state: { message: 'Email vérifié avec succès. Vous pouvez vous connecter.' } 
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.error || 
        'Code de vérification invalide ou expiré.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!userId) return null;

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Box sx={{ alignSelf: 'flex-end', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={toggleColorMode} color="inherit">
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
          <LanguageSwitcher />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Logo sx={{ height: 80, maxHeight: 80 }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              Vérification de l'email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Un code de vérification a été envoyé à <strong>{email}</strong>.
              Veuillez l'entrer ci-dessous pour activer votre compte.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Code de vérification (6 chiffres)"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              required
              margin="normal"
              autoFocus
              inputProps={{ 
                maxLength: 6,
                style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2rem' } 
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || code.length !== 6}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Vérifier'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}

export default VerifyEmail;
